import type { ReactNode } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { VoicePresetSelect } from './VoicePresetSelect';
import { useVoiceSettingsStyles } from './styles';
import {
  BASIC_PRESETS,
  LANGUAGE_CODE_PROVIDERS,
  VOICE_PROVIDER_PRESETS,
} from './voiceSettings.presets';
import type { VoiceFormValues } from './types';

type BasicSettingKey = keyof Pick<VoiceFormValues, 'model' | 'language'>;

interface Props {
  provider: string;
  providerOptions: readonly string[];
  voiceId: VoiceFormValues['voiceId'];
  model: VoiceFormValues['model'];
  language: VoiceFormValues['language'];
  disabled: boolean;
  providerError?: string;
  providerDisplayNameField?: ReactNode;
  onProviderChange: (value: string) => void;
  onVoiceIdChange: (value: string) => void;
  onSettingChange: (key: BasicSettingKey, value: VoiceFormValues[BasicSettingKey]) => void;
}

export const VoiceBasicSettings = ({
  provider,
  providerOptions,
  voiceId,
  model,
  language,
  disabled,
  providerError,
  providerDisplayNameField,
  onProviderChange,
  onVoiceIdChange,
  onSettingChange,
}: Props) => {
  const { t } = useTranslation();
  const { classes } = useVoiceSettingsStyles();
  const unsupported = t('superAdminManageVoice.basic.unsupported');
  const known = VOICE_PROVIDER_PRESETS.some((preset) => preset === provider);
  const languageCode = LANGUAGE_CODE_PROVIDERS.some((preset) => preset === provider);
  const presets = known ? BASIC_PRESETS[provider as keyof typeof BASIC_PRESETS] : undefined;
  const voiceChoices = presets?.voiceId ?? [];
  const options = (key: BasicSettingKey): readonly string[] => presets?.[key] ?? [];
  const select = (key: BasicSettingKey, value: VoiceFormValues[BasicSettingKey]) => {
    const choices = options(key);
    const unavailable = known && choices.length === 0;
    return (
      <VoicePresetSelect
        id={`voice-basic-${key}`}
        label={t(`superAdminManageVoice.advanced.fields.${key}`)}
        value={value ?? undefined}
        options={choices}
        disabled={disabled}
        unsupportedReason={unavailable ? unsupported : undefined}
        customHint={t('superAdminManageVoice.basic.customValue')}
        onChange={(next) => onSettingChange(key, next)}
      />
    );
  };
  return (
    <Box className={classes.fieldsGrid}>
      <TextField
        margin="dense"
        label={t('superAdminManageVoice.form.provider')}
        fullWidth
        select
        required
        value={provider}
        disabled={disabled}
        error={Boolean(providerError)}
        helperText={providerError}
        onChange={(event) => onProviderChange(event.target.value)}>
        <MenuItem value="">{t('superAdminManageVoice.form.none')}</MenuItem>
        {providerOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      {providerDisplayNameField}
      {voiceChoices.length > 0 ? (
        <VoicePresetSelect
          id="voice-basic-voiceId"
          label={t('superAdminManageVoice.form.voiceId')}
          value={voiceId || undefined}
          options={voiceChoices}
          disabled={disabled}
          customHint={t('superAdminManageVoice.basic.customValue')}
          onChange={(next) => onVoiceIdChange(next ?? '')}
        />
      ) : (
        <TextField
          margin="dense"
          label={t('superAdminManageVoice.form.voiceId')}
          fullWidth
          value={voiceId ?? ''}
          disabled={disabled || provider === 'hamsa'}
          helperText={
            provider === 'hamsa'
              ? unsupported
              : provider === 'deepgram'
                ? t('superAdminManageVoice.basic.deepgramVoice')
                : provider === 'azure'
                  ? t('superAdminManageVoice.basic.azureVoice')
                  : undefined
          }
          onChange={(event) => onVoiceIdChange(event.target.value)}
        />
      )}
      {select('model', model)}
      {languageCode ? (
        <TextField
          id="voice-basic-language"
          margin="dense"
          label={t('superAdminManageVoice.advanced.fields.language')}
          fullWidth
          value={language ?? ''}
          disabled={disabled}
          helperText={t('superAdminManageVoice.basic.languageCodeHint')}
          onChange={(event) => onSettingChange('language', event.target.value || null)}
        />
      ) : (
        select('language', language)
      )}
    </Box>
  );
};
